import React, { Fragment } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Input, Table, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useGetStoresQuery, useCreateStocktakeMutation } from '../../../../store/services/inventoryApi';
import { Trash, Info } from 'react-feather';
import ProductSearch from '../../../Common/ProductSearch';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    storeId: yup.string().required('Store is required'),
    type: yup.string().oneOf(['Full', 'Partial']).required('Stocktake type is required'),
    notes: yup.string(),
    items: yup.array().when('type', {
        is: 'Partial',
        then: schema => schema.of(
            yup.object().shape({
                productVariantId: yup.string().required(),
                productName: yup.string(),
                sku: yup.string(),
            })
        ).min(1, 'At least one item is required for partial stocktake'),
        otherwise: schema => schema.optional()
    })
});

const StocktakeForm = () => {
    const navigate = useNavigate();
    const { data: storesData } = useGetStoresQuery();
    const [createStocktake, { isLoading: isCreating }] = useCreateStocktakeMutation();

    const { control, register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            storeId: '',
            type: 'Full',
            notes: '',
            items: []
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    const stocktakeType = watch('type');
    const items = watch('items');
    const storeId = watch('storeId');

    const addItem = (variant) => {
        if (items.some(i => i.productVariantId === variant.productVariantId)) {
            toast.warning("Item already added");
            return;
        }
        append({
            productVariantId: variant.productVariantId,
            productName: `${variant.productName} (${variant.variantName})`,
            sku: variant.sku,
        });
    };

    const onSubmit = async (data) => {
        try {
            // Backend expects array of variant IDs if partial
            const payload = {
                ...data,
                items: data.type === 'Partial' ? data.items.map(i => ({ productVariantId: i.productVariantId })) : []
            };
            const result = await createStocktake(payload).unwrap();
            toast.success("Stocktake session started");
            navigate(`/inventory/stocktakes/session/${result._id || result.data?._id}`);
        } catch (err) {
            toast.error(err?.data?.message || "Failed to start stocktake");
        }
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Start Stocktake" parent="Inventory" title="New Session" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12" md={{ size: 8, offset: 2 }}>
                        <Card>
                            <CardHeader>
                                <h5>Initiate Stocktake Session</h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <FormGroup>
                                        <Label>Store</Label>
                                        <Controller
                                            name="storeId"
                                            control={control}
                                            render={({ field }) => (
                                                <Input type="select" {...field} invalid={!!errors.storeId}>
                                                    <option value="">-- Select Store --</option>
                                                    {storesData?.data?.map(w => (
                                                        <option key={w._id} value={w._id}>{w.storeName} {w.storeNumber ? `(${w.storeNumber})` : ''}</option>
                                                    ))}
                                                </Input>
                                            )}
                                        />
                                        <div className="invalid-feedback">{errors.storeId?.message}</div>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Stocktake Type</Label>
                                        <div className="d-flex gap-4">
                                            <FormGroup check inline>
                                                <Label check>
                                                    <input className="radio_animated" type="radio" value="Full" {...register('type')} /> Full Store
                                                </Label>
                                            </FormGroup>
                                            <FormGroup check inline>
                                                <Label check>
                                                    <input className="radio_animated" type="radio" value="Partial" {...register('type')} /> Partial (Select Items)
                                                </Label>
                                            </FormGroup>
                                        </div>
                                    </FormGroup>

                                    {stocktakeType === 'Full' && (
                                        <Alert color="info" className="d-flex align-items-start">
                                            <Info size={18} className="me-2 mt-1" />
                                            <div className="flex-grow-1">
                                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; A <strong>Full</strong> stocktake will include all items currently in the system for this store.
                                                The system will take a snapshot of quantities when you click "Start Session".
                                            </div>
                                        </Alert>
                                    )}

                                    {stocktakeType === 'Partial' && (
                                        <div className="mt-4">
                                            <h6>Select Items to Count</h6>
                                            {!storeId ? (
                                                <Alert color="warning" className="py-2 small d-flex align-items-center">
                                                    <Info size={15} className="me-2 flex-shrink-0" />
                                                    Please select a <strong>&nbsp;Store&nbsp;</strong> above before choosing products.
                                                </Alert>
                                            ) : (
                                                <div className="mb-3">
                                                    <ProductSearch
                                                        onProductSelect={addItem}
                                                        placeholder="Search products..."
                                                        selectedIds={fields.map(f => f.productVariantId)}
                                                        storeId={storeId}
                                                    />
                                                </div>
                                            )}

                                            <Table responsive bordered hover size="sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Product</th>
                                                        <th>SKU</th>
                                                        <th style={{ width: '50px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fields.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>{item.productName}</td>
                                                            <td>{item.sku}</td>
                                                            <td>
                                                                <Button color="danger" size="xs" onClick={() => remove(index)}><Trash size={14} /></Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {fields.length === 0 && (
                                                        <tr>
                                                            <td colSpan="3" className="text-center text-muted">No items selected yet.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                            {errors.items && <div className="text-danger small">{errors.items.message}</div>}
                                        </div>
                                    )}

                                    <FormGroup className="mt-4">
                                        <Label>Notes</Label>
                                        <Input type="textarea" {...register('notes')} placeholder="Optional notes about this stocktake session..." />
                                    </FormGroup>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <Button color="light" onClick={() => navigate('/inventory/stocktakes')}>Cancel</Button>
                                        <Button color="primary" type="submit" disabled={isCreating}>
                                            {isCreating ? <Spinner size="sm" /> : 'Start Counting Session'}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StocktakeForm;
